
import { defineComponent, onBeforeMount, reactive } from 'vue'
import { useRouter } from 'vue-router'

// Components
import PageTitle from '@/common/components/UI/PageTitle/index.vue'
import TheButton from '@/common/components/UI/TheButton/index.vue'
import TheInput from '@/common/components/UI/TheInput/index.vue'
import TheSelect from '@/common/components/UI/TheSelect/index.vue'
import Checkbox from '@/common/components/UI/Checkbox/index.vue'

// Classes
import { DataState } from '@/core/classes/DataState.class'

// Types
import { UnitT, TmcCreatePayload } from '@/core/types/tmc.types'

// Realisation
import { tmc } from '@/core/realisations/tmc.realisation'

// Validation
import useVuelidate from '@vuelidate/core'
import { required } from '@vuelidate/validators'

// Composable
import useNotifications from '@/common/composable/useNotifications'

export default defineComponent({
    components: {
        PageTitle,
        TheButton,
        TheInput,
        TheSelect,
        Checkbox,
    },
    setup() {
        const router = useRouter()
        const notifications = useNotifications()

        const state = reactive({
            code_1c: null as string | null,
            name: null as string | null,
            short_name: null as string | null,
            unit_id: null as number | null,
            isSecondMaterial: false,
            //
            is_loading: false,
        })

        const state_validation = useVuelidate(
            {
                name: { required },
                short_name: { required },
                unit_id: { required },
            },
            state
        )

        const data_state = reactive({
            units: new DataState<UnitT>({}),
        })

        const loadUnits = async (page: number, search?: string) => {
            data_state.units.pagination.current_page = page

            try {
                data_state.units.is_loading = true

                const response = await tmc.getUnitsList(page, search)
                data_state.units.list = response.data
                data_state.units.pagination.total_pages = response.meta.last_page

                data_state.units.is_failed = false
                data_state.units.failure.error_state.is_cancel = false
            } catch (error) {
                data_state.units.failure.catchError(error)

                if (!data_state.units.failure.error_state.is_cancel) {
                    data_state.units.is_failed = true
                }
            } finally {
                if (!data_state.units.failure.error_state.is_cancel) {
                    data_state.units.is_loading = false
                }
            }
        }

        const loadMoreUnits = async (page: number) => {
            if (page > data_state.units.pagination.total_pages) return
            data_state.units.pagination.current_page = page

            try {
                data_state.units.is_loading = true

                const response = await tmc.getUnitsList(page)
                data_state.units.list = data_state.units.list.concat(response.data)

                data_state.units.pagination.total_pages = response.meta.last_page

                data_state.units.is_failed = false
                data_state.units.failure.error_state.is_cancel = false
            } catch (error) {
                data_state.units.failure.catchError(error)

                if (!data_state.units.failure.error_state.is_cancel) {
                    data_state.units.is_failed = true
                }
            } finally {
                if (!data_state.units.failure.error_state.is_cancel) {
                    data_state.units.is_loading = false
                }
            }
        }

        const searchUnits = (search_query: string) => {
            // data_state.companies.pagination.current_page
            loadUnits(1, search_query)
        }

        const unshiftFindedUnit = async (unit: UnitT) => {
            await loadUnits(data_state.units.pagination.current_page)
            data_state.units.list.unshift(unit)
        }

        onBeforeMount(() => {
            loadUnits(1)
        })

        const submitForm = async () => {
            if (state.is_loading) return

            state_validation.value.$touch()
            if (state_validation.value.$invalid) return

            try {
                state.is_loading = true

                const payload: TmcCreatePayload = {
                    code1c: state.code_1c as string,
                    name: state.name as string,
                    shortName: state.short_name as string,
                    unitId: state.unit_id as number,
                    isSecondMaterial: state.isSecondMaterial as boolean,
                }

                await tmc.createTMC(payload)

                notifications.notify({ title: 'ТМЦ создан', type: 'success' })
                router.push('/tmc')
            } catch (error) {
                console.error(error)
                notifications.notifyError({ error, title: 'При создании ТМЦ произошла ошибка' })
            } finally {
                state.is_loading = false
            }
        }

        return {
            state,
            state_validation,
            data_state,
            submitForm,
            loadUnits,
            loadMoreUnits,
            searchUnits,
            unshiftFindedUnit,
        }
    },
})
